import React, { useEffect, useState } from "react";
import { DEFAULT_CATEGORIES } from "../data/models.js";

function todayDateString() {
  const localDate = new Date();
  const year = localDate.getFullYear();
  const month = String(localDate.getMonth() + 1).padStart(2, "0");
  const day = String(localDate.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function mapExpenseToFormValues(expense, currentUserId) {
  if (!expense) {
    return {
      date: todayDateString(),
      description: "",
      amount: "",
      paidBy: "me",
      splitType: "fifty-fifty",
      customAmount: "",
      category: DEFAULT_CATEGORIES[0],
    };
  }

  return {
    date: expense.date || todayDateString(),
    description: expense.description || "",
    amount: String(expense.amount ?? ""),
    paidBy: expense.paid_by === currentUserId ? "me" : "partner",
    splitType: expense.split_amount === null ? "fifty-fifty" : "custom",
    customAmount:
      expense.split_amount === null || expense.split_amount === undefined
        ? ""
        : String(expense.split_amount),
    category: expense.category || DEFAULT_CATEGORIES[0],
  };
}

function AddExpenseForm({
  onAddExpense,
  onUpdateExpense,
  expenseToEdit = null,
  onCancelEdit,
  disabled,
  couple,
  currentUserId,
  showCategory = false,
}) {
  const [form, setForm] = useState(() =>
    mapExpenseToFormValues(expenseToEdit, currentUserId),
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationError, setValidationError] = useState("");

  useEffect(() => {
    setForm(mapExpenseToFormValues(expenseToEdit, currentUserId));
    setValidationError("");
  }, [expenseToEdit, currentUserId]);

  const isEditMode = Boolean(expenseToEdit);
  const partnerMember = couple?.members?.find((m) => m.user_id !== currentUserId);
  const partnerId = partnerMember?.user_id;
  const partnerName = partnerMember?.display_name || "Partner";

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const validate = () => {
    if (!form.description.trim()) return "Description is required.";
    if (!form.amount || Number(form.amount) <= 0) return "Amount must be greater than 0.";
    if (form.paidBy === "partner" && !partnerId) return "Invite your partner before assigning partner-paid expenses.";
    if (showCategory && !form.category) return "Category is required.";
    if (form.splitType === "custom" && (!form.customAmount || Number(form.customAmount) <= 0)) {
      return "Custom owed amount must be greater than 0.";
    }
    return "";
  };

  const resetToDefault = () => {
    setForm(mapExpenseToFormValues(null, currentUserId));
    setValidationError("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const validationMessage = validate();
    if (validationMessage) {
      setValidationError(validationMessage);
      return;
    }

    setValidationError("");
    setIsSubmitting(true);

    const expensePayload = {
      date: form.date,
      description: form.description.trim(),
      amount: parseFloat(form.amount),
      paid_by: form.paidBy === "me" ? currentUserId : partnerId,
      split_amount:
        form.splitType === "custom" && form.customAmount
          ? parseFloat(form.customAmount)
          : null,
      category: showCategory ? form.category : undefined,
    };

    try {
      if (isEditMode && onUpdateExpense) {
        await onUpdateExpense(expenseToEdit.id, expensePayload);
      } else {
        await onAddExpense(expensePayload);
        resetToDefault();
      }
    } catch (error) {
      console.error("Error saving expense:", error);
      setValidationError("Could not save expense. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className="add-expense-form" onSubmit={handleSubmit}>
      <h2>{isEditMode ? "Edit Expense" : "Add Expense"}</h2>

      {validationError && (
        <div className="auth-error" style={{ marginBottom: "12px" }}>
          {validationError}
        </div>
      )}

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="date">Date</label>
          <input
            type="date"
            id="date"
            value={form.date}
            onChange={(e) => setField("date", e.target.value)}
            disabled={disabled || isSubmitting}
          />
        </div>

        <div className="form-group">
          <label htmlFor="amount">Amount ($)</label>
          <input
            type="number"
            inputMode="decimal"
            id="amount"
            value={form.amount}
            onChange={(e) => setField("amount", e.target.value)}
            placeholder="0.00"
            step="0.01"
            min="0"
            disabled={disabled || isSubmitting}
            required
          />
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="description">Description</label>
        <input
          type="text"
          id="description"
          value={form.description}
          onChange={(e) => setField("description", e.target.value)}
          placeholder="What was this for?"
          disabled={disabled || isSubmitting}
          required
        />
      </div>

      {showCategory && (
        <div className="form-group">
          <label htmlFor="category">Category</label>
          <select
            id="category"
            value={form.category}
            onChange={(e) => setField("category", e.target.value)}
            disabled={disabled || isSubmitting}
          >
            {DEFAULT_CATEGORIES.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="form-group">
        <label>Who paid?</label>
        <div className="toggle-buttons">
          <button
            type="button"
            className={`toggle-btn ${form.paidBy === "me" ? "active me" : ""}`}
            onClick={() => setField("paidBy", "me")}
            disabled={disabled || isSubmitting}
          >
            I paid
          </button>
          <button
            type="button"
            className={`toggle-btn ${form.paidBy === "partner" ? "active girlfriend" : ""}`}
            onClick={() => setField("paidBy", "partner")}
            disabled={disabled || isSubmitting}
          >
            {partnerName} paid
          </button>
        </div>
      </div>

      <div className="form-group">
        <label>Split type</label>
        <div className="toggle-buttons">
          <button
            type="button"
            className={`toggle-btn ${form.splitType === "fifty-fifty" ? "active" : ""}`}
            onClick={() => setField("splitType", "fifty-fifty")}
            disabled={disabled || isSubmitting}
          >
            50/50
          </button>
          <button
            type="button"
            className={`toggle-btn ${form.splitType === "custom" ? "active" : ""}`}
            onClick={() => setField("splitType", "custom")}
            disabled={disabled || isSubmitting}
          >
            Full Reimbursement
          </button>
        </div>
      </div>

      {form.splitType === "custom" && (
        <div className="form-group custom-split">
          <label htmlFor="customAmount">
            {form.paidBy === "me" ? `${partnerName} owes ($)` : `You owe ${partnerName} ($)`}
          </label>
          <input
            type="number"
            inputMode="decimal"
            id="customAmount"
            value={form.customAmount}
            onChange={(e) => setField("customAmount", e.target.value)}
            placeholder="0.00"
            step="0.01"
            min="0"
            disabled={disabled || isSubmitting}
          />
        </div>
      )}

      <div style={{ display: "flex", gap: "8px" }}>
        <button type="submit" className="submit-btn" disabled={disabled || isSubmitting}>
          {isSubmitting ? "Saving..." : isEditMode ? "Save Changes" : "Add Expense"}
        </button>
        {isEditMode && (
          <button
            type="button"
            className="cancel-btn"
            onClick={onCancelEdit}
            disabled={isSubmitting}
          >
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}

export default AddExpenseForm;
